/**
 * BY MANAY cart drawer: 10-minute visual countdown (MM:SS), resets at 00:00.
 * Single interval per element; cleaned up on disconnect.
 */
class BymanayCartDrawerTimer extends HTMLElement {
  /** @type {number | null} */
  #intervalId = null;

  /** @type {number} */
  #remainingSeconds = 600;

  static get observedAttributes() {
    return ['data-active'];
  }

  connectedCallback() {
    this.#syncFromAttribute();
    if (this.dataset.active === 'true') {
      this.#start();
    }
  }

  disconnectedCallback() {
    this.#stop();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-active' || oldValue === newValue) return;
    if (newValue === 'true') {
      this.#start();
    } else {
      this.#stop();
    }
  }

  /** @param {boolean} active */
  setActive(active) {
    this.dataset.active = active ? 'true' : 'false';
  }

  #syncFromAttribute() {
    const display = this.querySelector('[data-bymanay-cart-timer-display]');
    if (display) display.textContent = this.#format(this.#remainingSeconds);
  }

  #start() {
    if (this.#intervalId != null) return;
    this.#syncFromAttribute();
    this.#intervalId = window.setInterval(() => {
      this.#remainingSeconds -= 1;
      if (this.#remainingSeconds <= 0) {
        this.#remainingSeconds = 600;
      }
      const display = this.querySelector('[data-bymanay-cart-timer-display]');
      if (display) display.textContent = this.#format(this.#remainingSeconds);
    }, 1000);
  }

  #stop() {
    if (this.#intervalId != null) {
      clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
  }

  /** @param {number} totalSeconds */
  #format(totalSeconds) {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
}

class BymanayCartDrawerController extends HTMLElement {
  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  #drawer = null;

  /** @type {((event: Event) => void) | null} */
  #onOpen = null;

  /** @type {((event: Event) => void) | null} */
  #onClose = null;

  connectedCallback() {
    this.#drawer = document.getElementById('cart-drawer');
    const timer = this.querySelector('bymanay-cart-drawer-timer');
    if (!this.#drawer || !(timer instanceof BymanayCartDrawerTimer)) return;

    this.#onOpen = () => timer.setActive(true);
    this.#onClose = () => timer.setActive(false);

    this.#drawer.addEventListener('theme-drawer:open', this.#onOpen);
    this.#drawer.addEventListener('theme-drawer:close', this.#onClose);

    if (this.#drawer.hasAttribute('open')) {
      timer.setActive(true);
    }
  }

  disconnectedCallback() {
    if (!this.#drawer) return;
    if (this.#onOpen) this.#drawer.removeEventListener('theme-drawer:open', this.#onOpen);
    if (this.#onClose) this.#drawer.removeEventListener('theme-drawer:close', this.#onClose);
  }
}

if (!customElements.get('bymanay-cart-drawer-timer')) {
  customElements.define('bymanay-cart-drawer-timer', BymanayCartDrawerTimer);
}
if (!customElements.get('bymanay-cart-drawer-controller')) {
  customElements.define('bymanay-cart-drawer-controller', BymanayCartDrawerController);
}
